<template>
    <Loading v-if="availability === SeatAvailability.InProgress" />
    <div
        v-else-if="
            availability === SeatAvailability.NotAvailable ||
            availability === SeatAvailability.Unknown
        " />
    <div v-else-if="hasSeating">
        <Addon
            v-model="selected"
            :onUpdate:modelValue="(value: any) => handleChange(value)"
            :title="t({ key: 'seating.addonHeadline' })"
            :padding="false"
            :errors="field.errors.value"
            :field-error="field.errors.value.length > 0"
            :illustration="{ type: 'Seating3' }">
            <template #content>
                <div class="seating-content">
                    <div class="seating-content-text">
                        {{ t({ key: 'seating.seatingText' }) }}
                    </div>

                    <SkeletonLoading v-if="isUpdating" class="seating-content-loading" />
                    <div v-else-if="totalPrice" class="seating-content-selection">
                        <span class="seating-content-selected">
                            {{ getMoneyObjectAsString(totalPrice) }} /
                            {{ allSeats.length }}
                            {{ t({ key: 'seating.seats' }) }}
                        </span>

                        <ButtonLink :onClick="onSelect" size="m" :automaticPosition="false">
                            {{ t({ key: 'seating.changeSeats' }) }}
                        </ButtonLink>
                    </div>
                </div>
            </template>
        </Addon>
    </div>

    <SeatMap
        v-if="product.Gordian"
        display="modal"
        :value="product.Pax"
        :flightProduct="product"
        :seating="product.Gordian" />
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { PropType, ref, defineComponent, computed, watch } from 'vue';
    import { useStore } from '@/store';
    import { trackClarity } from '@/tracking/clarity';
    import { ClarityEvent } from '@/tracking/clarity/types';
    import { logAnalyticsGordianSeatingChanged } from '@/tracking/analytics/Checkout/Functions/checkout.functions';
    import { endpoints } from '@/api/endpoints';
    import { useField } from 'vee-validate';
    import {
        getAllSeatsForFlightProduct,
        getSeatingTotalPriceForFlightProduct,
    } from '@/helpers/seating';
    import { getSeatingFieldName } from './Seating.helpers';
    import { isEmpty } from 'lodash';
    import {
        FlightProduct,
        GordianSeating,
        GordianWidgetStatus,
        SeatAvailability,
    } from '@/api/Cart/cart.interfaces';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { Money } from '@libs/money/money.interfaces';
    import SeatMap from '@/components/shared/GordianSeatMap/Components/Seatmap.vue';
    import SkeletonLoading from '@/components/shared/SkeletonLoading.vue';
    import Addon from '@ui/components/Addon/Addon.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';
    import Loading from './Loading/Loading.vue';
    import { logStatsigGordianSeatingChanged } from '@/tracking/statsig/checkout.functions';

    export default defineComponent({
        name: 'Seating',
        methods: { t, getMoneyObjectAsString },
        components: {
            Addon,
            ButtonLink,
            SeatMap,
            SkeletonLoading,
            Loading,
        },
        props: {
            id: { type: String, required: true },
            product: {
                type: Object as PropType<FlightProduct>,
                required: true,
            },
        },
        setup(props) {
            const store = useStore();

            const fieldName = computed<string>(() => getSeatingFieldName());
            const field: any = useField(fieldName.value);

            const selected = ref<boolean | undefined>(undefined);

            const seating = computed<GordianSeating | null>(() => props.product.Gordian);

            const hasSeating = computed<boolean>(() =>
                Boolean(seating.value && seating.value.TripId && seating.value.SearchId)
            );

            const availability = computed<SeatAvailability | null | undefined>(
                () => seating.value?.SeatAvailability
            );

            const tripId = computed<string | null | undefined>(() => seating.value?.TripId);

            const totalPrice = computed<Money | undefined>(
                () => getSeatingTotalPriceForFlightProduct(props.product) ?? undefined
            );

            const allSeats = computed(() => getAllSeatsForFlightProduct(props.product));

            const isUpdating = computed(
                () => props.product.Gordian?.WidgetStatus === GordianWidgetStatus.UPDATING
            );

            const dispatch = {
                toggleActive: (active = true) =>
                    store.dispatch('cart/setSeatingActive', {
                        tripId: tripId.value,
                        active,
                    }),
                setUpdating: () =>
                    store.dispatch('cart/setSeatingUpdating', {
                        tripId: tripId.value,
                        updating: true,
                    }),
                setUpdatingComplete: () =>
                    store.dispatch('cart/setSeatingUpdating', {
                        tripId: tripId.value,
                        updating: false,
                    }),
                clearPaxSeating: (flightProduct: FlightProduct) =>
                    store.dispatch('cart/clearPaxSeating', flightProduct),
            };

            watch(
                () => field.value.value,
                (newValue) => {
                    if (newValue !== selected.value) {
                        selected.value = newValue;
                    }
                },
                { immediate: true }
            );

            watch(selected, (newValue) => {
                field.setValue(newValue);
                handleTouchedField(newValue);
            });

            watch(
                () => props.product.Gordian?.Active,
                (active) => {
                    if (active === false && isEmpty(allSeats.value)) {
                        selected.value = false;
                    }
                }
            );

            watch(
                allSeats,
                (seats, prevSeats) => {
                    if (prevSeats) {
                        if (seats.length !== prevSeats.length) {
                            selected.value = allSeats.value.length > 0;
                        }
                        trackClarity(
                            ClarityEvent.CHECKOUT_ADDON_SEATING_GORDIAN_SEAT_CHANGED
                        );
                        logAnalyticsGordianSeatingChanged(props.product, seats, prevSeats);
                        logStatsigGordianSeatingChanged(props.product, seats, prevSeats);
                    } else if (!prevSeats && allSeats.value.length > 0) {
                        selected.value = true;
                    }
                },
                { immediate: true }
            );

            function handleTouchedField(value: boolean | undefined) {
                store.dispatch('checkout/setTouchedField', {
                    id: props.id,
                    name: fieldName.value,
                    value,
                });
            }

            function handleChange(value: boolean) {
                value ? onSelect() : onUnselect();
            }

            function onSelect() {
                dispatch.toggleActive();
            }

            function onUnselect() {
                dispatch.toggleActive(false).then(onUnselectSeating);
            }

            function onUnselectSeating() {
                if (allSeats.value.length) {
                    dispatch.setUpdating().then(() =>
                        endpoints.seating
                            .ClearSeats(props.product)
                            .then(dispatch.clearPaxSeating)
                            .catch(() => {
                                trackClarity(ClarityEvent.CHECKOUT_ADDON_SEATING_ERROR);
                            })
                            .finally(dispatch.setUpdatingComplete)
                    );
                }
            }

            return {
                selected,
                SeatAvailability,
                availability,
                allSeats,
                field,
                isUpdating,
                onSelect,
                hasSeating,
                onUnselect,
                totalPrice,
                handleTouchedField,
                handleChange,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Seating.scss';
</style>
